import React, { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useFlags } from '@atlaskit/flag/use-flags';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import type { MediaClientConfig } from '@atlaskit/media-core/auth';
import type {
	UploadEndEventPayload,
	UploadErrorEventPayload,
	UploadRejectionData,
} from '@atlaskit/media-picker/types';
import { token } from '@atlaskit/tokens';

import { useProductDependencies } from '../../../controllers/use-product-dependencies';

import { messages } from './messages';
import { getAttachmentSizeLimitErrorFlag } from './utils';

type MediaConfigType = {
	authProvider: MediaClientConfig['authProvider'];
	collection: string | undefined;
	onUploadRejection: ((data: UploadRejectionData) => boolean) | undefined;
} | null;

interface UseUploadMediaConfigProps {
	analyticsSource: string;
	contentId: string;
	handleUploadedImageSelection: (data: { altDescription: string; uploadedImageId: string }) => void;
}

export const useUploadMediaConfig = ({
	contentId,
	handleUploadedImageSelection,
	analyticsSource,
}: UseUploadMediaConfigProps): {
	closeMediaBrowser: () => void;
	handleOnError: (payload: UploadErrorEventPayload) => void;
	handleUploadEnd: (event: UploadEndEventPayload) => void;
	handleUploadStart: () => void;
	isMediaBrowserOpen: boolean;
	isUploadingImage: boolean;
	mediaConfig: MediaConfigType;
	openMediaBrowser: () => void;
} => {
	const { createEditorMediaProvider, mediaUploadExperienceTracker, transformUploadedImageId } =
		useProductDependencies();
	const [uploadedImageId, setUploadedImageId] = useState<string | null>(null);
	const { showFlag } = useFlags();
	const intl = useIntl();
	const { formatMessage } = intl;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isUploadingImage, setIsUploadingImage] = useState<boolean>(false);
	const [isMediaBrowserOpen, setIsMediaBrowserOpen] = useState<boolean>(false);
	const [mediaConfig, setMediaConfig] = useState<MediaConfigType>(null);
	useEffect(() => {
		if (!!uploadedImageId) {
			handleUploadedImageSelection({
				uploadedImageId,
				altDescription: '',
			});
		}
		// We only want this logic to run when uploadedImageId changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [uploadedImageId]);

	const openMediaBrowser = (): void => {
		setIsMediaBrowserOpen(true);
	};

	const closeMediaBrowser = (): void => {
		setIsMediaBrowserOpen(false);
	};

	const handleUploadStart = (): void => {
		setIsUploadingImage(true);
		mediaUploadExperienceTracker?.start({ analyticsSource });
	};

	const showErrorFlag = useCallback(() => {
		void showFlag({
			title: formatMessage(messages.errorFlagTitle),
			description: formatMessage(messages.errorFlagDescription),
			icon: <StatusErrorIcon label="" size="medium" color={token('color.icon.danger')} />,
		});

		setIsUploadingImage(false);
	}, [formatMessage, showFlag]);

	const handleOnError = (payload: UploadErrorEventPayload): void => {
		setIsUploadingImage(false);
		showErrorFlag();
		mediaUploadExperienceTracker?.stopOnError({
			error: payload?.error?.rawError ?? new Error('Error with cards image upload'),
		});
	};

	const handleUploadEnd = (event: UploadEndEventPayload): void => {
		setIsUploadingImage(false);
		const fileType = event?.file?.type;
		const id = event?.file?.id;

		if (!fileType.includes('image')) {
			showErrorFlag();
			mediaUploadExperienceTracker?.stopOnError({
				error: new Error('File type is not image'),
			});
		} else if (id) {
			const imageId = transformUploadedImageId
				? transformUploadedImageId({ contentId, fileId: id })
				: id;

			setUploadedImageId(imageId);
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'selected',
					actionSubject: 'image',
					source: analyticsSource,
					attributes: {
						imageSource: 'customerUpload',
					},
				},
			}).fire();
			mediaUploadExperienceTracker?.succeed();
		} else {
			showErrorFlag();
			mediaUploadExperienceTracker?.stopOnError({
				error: new Error('File does not have a type or id'),
			});
		}
	};

	useEffect(() => {
		const showAttachmentLimitReachedFlag = (rejectionData: UploadRejectionData) => {
			if (rejectionData) {
				void showFlag(getAttachmentSizeLimitErrorFlag(rejectionData, intl));
				setIsUploadingImage(false);
				mediaUploadExperienceTracker?.succeed();
			}
			return !!rejectionData;
		};

		const createMediaConfig = async () => {
			try {
				const { uploadParams, uploadMediaClientConfig } =
					(await createEditorMediaProvider?.({
						contentId,
						contentType: 'page',
						isExternalShare: false,
						onUploadRejection: showAttachmentLimitReachedFlag,
					})) || {};

				if (uploadMediaClientConfig === undefined) {
					return null;
				}
				setMediaConfig({
					collection: uploadParams?.collection,
					authProvider: uploadMediaClientConfig.authProvider,
					onUploadRejection: uploadParams?.onUploadRejection,
				});
			} catch (error) {
				mediaUploadExperienceTracker?.stopOnError({
					error: new Error(`Error with image media provider: ${error}`),
				});
			}
		};
		void createMediaConfig();
	}, [
		contentId,
		showErrorFlag,
		showFlag,
		intl,
		mediaUploadExperienceTracker,
		createEditorMediaProvider,
	]);

	return {
		mediaConfig,
		handleUploadEnd,
		handleUploadStart,
		handleOnError,
		isMediaBrowserOpen,
		openMediaBrowser,
		closeMediaBrowser,
		isUploadingImage,
	};
};
